/**
 * 类型树 —— 结构化数据之间的连接与**裁剪**。
 *
 * ## 为什么不是「类型相等才能连」
 *
 * `component` 和 `page` 都是「有布局数据的东西」，它们之间传值是有意义的
 * （只传布局那一部分）。用相等来判断，这种连接永远连不上。
 *
 * 所以：**连接看最近共同祖先（MSCA），传值时向上裁剪到那个祖先** ——
 * 丢掉祖先没声明的字段。这跟嵌套面板共享「布局」这一层是一个意思。
 *
 * ## 只做单继承
 *
 * 多继承下 MSCA 可能**不唯一**：
 *
 *       A       B
 *        \     /
 *         C   D
 *          \ /
 *           E          E 的共同祖先是 A 和 B，两者互不包含 → 「最近的」没有答案
 *
 * 单继承是一棵树，MSCA 唯一、判定简单，而且 `component` / `page` 这种例子完全够用。
 * 真需要「同时具备两种能力」时，应该引入**接口 / mixin**（不参与 MSCA、只做标记），
 * 而不是把继承图变成 DAG。
 *
 * ## 原子类型不参与裁剪
 *
 * ⚠️ 这条限制是必须的：`text` 和 `struct` 的最近共同祖先是 `scalar`，
 * 不加限制的话，**一个文本就能接到组件端口上** —— 明显是错的。
 * 所以只有**结构化子树内**的共同祖先才允许裁剪（见 `structuredRoot`）。
 */
import type { SocketType } from './socket'

export interface TypeNodeDef {
  id: string
  /** 单一父级。不写 = 根 */
  extends?: string
}

export interface TypeTree {
  nodes: ReadonlyMap<string, TypeNodeDef>
  /**
   * 结构化数据子树的根（我们这里是 `struct`）。
   * 只有它**子树内**的共同祖先才允许「裁剪后传递」。
   */
  structuredRoot: string
  /** 通配类型：任意一侧是它就放行（我们这里是 `any`） */
  wildcard: string
}

export function buildTypeTree(
  defs: readonly TypeNodeDef[],
  options: { structuredRoot: string; wildcard: string },
): TypeTree {
  const nodes = new Map<string, TypeNodeDef>()
  for (const def of defs) nodes.set(def.id, def)
  return { nodes, structuredRoot: options.structuredRoot, wildcard: options.wildcard }
}

/**
 * 类型树的定义问题（悬空父级 / 环 / 缺根）。
 *
 * 单独一个函数而不是在 build 里抛错：类型树是集成方给的**数据**，
 * 报出来让他能看见，比启动即崩友好。
 */
export function typeTreeProblems(tree: TypeTree): string[] {
  const problems: string[] = []

  if (!tree.nodes.has(tree.structuredRoot)) {
    problems.push(`结构化根「${tree.structuredRoot}」不在类型树里`)
  }
  if (tree.nodes.has(tree.wildcard)) {
    problems.push(`通配类型「${tree.wildcard}」不该出现在树里（它是特例，不是节点）`)
  }

  for (const [id, def] of tree.nodes) {
    if (def.extends && !tree.nodes.has(def.extends)) {
      problems.push(`「${id}」的父级「${def.extends}」不存在`)
    }
    // 顺链走一遍，能回到自己就是环
    const seen = new Set<string>([id])
    let cursor = def.extends
    while (cursor) {
      if (seen.has(cursor)) {
        problems.push(`「${id}」的继承链成环`)
        break
      }
      seen.add(cursor)
      cursor = tree.nodes.get(cursor)?.extends
    }
  }

  return problems
}

/** 从自己到根的链（含自己）。自带防环，坏数据也不会死循环 */
export function chainOf(tree: TypeTree, id: string): string[] {
  const chain: string[] = []
  const seen = new Set<string>()
  let cursor: string | undefined = id

  while (cursor && !seen.has(cursor)) {
    seen.add(cursor)
    chain.push(cursor)
    cursor = tree.nodes.get(cursor)?.extends
  }
  return chain
}

/** `ancestor` 是不是 `of` 的**真**祖先（自己不算） */
export function isAncestor(tree: TypeTree, ancestor: string, of: string): boolean {
  if (ancestor === of) return false
  return chainOf(tree, of).includes(ancestor)
}

/**
 * 最近共同祖先。没有共同祖先（不在同一棵树里）返回 null。
 *
 * 单继承下有个漂亮的性质：**`a` 自己到根的链上，第一个也出现在 `b` 的链上的就是答案** ——
 * 不需要遍历所有祖先再挑「最深」的。
 */
export function mostSpecificCommonAncestor(
  tree: TypeTree,
  a: string,
  b: string,
): string | null {
  const chainB = new Set(chainOf(tree, b))
  for (const id of chainOf(tree, a)) {
    if (chainB.has(id)) return id
  }
  return null
}

/** 是否属于结构化数据子树（含根） */
export function isStructured(tree: TypeTree, id: string): boolean {
  return id === tree.structuredRoot || isAncestor(tree, tree.structuredRoot, id)
}

/* ══════════════════════════════════════════════════════════════
   连接判定
   ══════════════════════════════════════════════════════════════ */

export type Placement =
  | { ok: false; reason: 'unrelated' | 'atomic-mismatch' | 'narrowing' }
  /**
   * 精确连接的端口只接受完全相同的类型（见 `SocketDef.strict`）。
   *
   * ⚠️ 单列一个原因而不是复用 `narrowing`：这两种"不行"给用户的提示
   * 完全不同 —— 「类型不兼容」和「这个口子只收一模一样的东西」。
   */
  | { ok: false; reason: 'strict' }
  /** 任一侧是通配类型 */
  | { ok: true; kind: 'wildcard' }
  | { ok: true; kind: 'same' }
  /** `from` 是 `to` 的祖先：值本来就「是」它，不裁剪 */
  | { ok: true; kind: 'upcast' }
  /**
   * 裁剪到共同祖先 `via`。
   *
   * `lossy` = 共同祖先是结构化**根**（几乎没有字段）——
   * 技术上合法，但**大概率是接错了**。编辑器该给警告而不是拦死。
   */
  | { ok: true; kind: 'slice'; via: string; lossy: boolean }
/**
 * 判定 `from` 的值能不能流进 `to`，以及要裁剪成什么。
 *
 * ⚠️ 方向别搞反：`from` 是**发出去的值的类型**，`to` 是**接收端声明的类型**。
 * 安全的前提是「发出的值**是**接收端要的那种」（向上转型）。
 *
 * 规则：
 *   1. 任一侧是通配类型         → 放行
 *   2. 类型相同                 → 放行
 *   3. `to` 是 `from` 的祖先     → 放行（向上转型，**不裁剪**：值本来就「是」它）
 *   4. `from` 是 `to` 的祖先     → **不放行**（向下转型，见下）
 *   5. 共同祖先在**结构化子树内** → 放行，裁剪到那个共同祖先
 *   6. 其余                     → 不放行
 *
 * 第 4 条要单独拦：`struct` 接到 `component` 端口上时，两条链的共同祖先是
 * `struct` 自己 —— 光看第 5 条会放行，但那是把一个通用结构塞进专用端口，
 * 它未必有 component 的字段。那是「猜」不是「转型」。
 *
 * 第 5 条就是你要的：`component` 和 `page` 共享 `layout`，
 * 于是能互传，**只传布局那部分**。
 */
export function placementOf(tree: TypeTree, from: string, to: string): Placement {
  if (from === tree.wildcard || to === tree.wildcard) return { ok: true, kind: 'wildcard' }
  if (from === to) return { ok: true, kind: 'same' }

  // 接收端要的是「发出去的值」的祖先 → 安全，而且无损
  if (isAncestor(tree, to, from)) return { ok: true, kind: 'upcast' }
  // 反过来是向下转型 → 拦掉（先判这里，否则会被第 5 条误放行）
  if (isAncestor(tree, from, to)) return { ok: false, reason: 'narrowing' }

  const via = mostSpecificCommonAncestor(tree, from, to)
  if (!via) return { ok: false, reason: 'unrelated' }

  /*
   * ★ 只有结构化子树内的共同祖先才允许裁剪。
   * 不加这条的话，`text` 和 `struct` 会经由 `scalar` 判定通过 ——
   * 一个文本就能接到组件端口上。
   */
  if (!isStructured(tree, via)) return { ok: false, reason: 'atomic-mismatch' }

  return { ok: true, kind: 'slice', via, lossy: via === tree.structuredRoot }
}

/** 类型 id 是否是树里登记过的（`any` 等特例不算） */
export function isKnownType(tree: TypeTree, id: SocketType | string): boolean {
  return tree.nodes.has(id)
}
